import { WorkOrderSyncAction } from '@atlas/types'
import { EmptyState } from '@atlas/ui-components'
import { Badge, type BadgeProps } from '@atlas/ui-primitives'
import { Link } from 'react-router-dom'
import { fieldLabel, formatFieldValue } from '@/domain/work-order-history'
import { formatUtc } from '@/lib/format-utc'
import type { WorkOrderSyncActionDTO } from '@/services/work-order-sync.service'
import { humanize } from './StatusBadges'

type Variant = NonNullable<BadgeProps['variant']>

const ACTION_VARIANT: Record<WorkOrderSyncAction, Variant> = {
	[WorkOrderSyncAction.CREATE]: 'success',
	[WorkOrderSyncAction.UPDATE]: 'secondary',
	[WorkOrderSyncAction.FLAG_CLOSED]: 'muted',
	[WorkOrderSyncAction.FLAG_REMOVED]: 'danger',
	[WorkOrderSyncAction.HOLD]: 'warning',
	[WorkOrderSyncAction.NONE]: 'muted',
}

export interface SyncActionsListProps {
	actions: WorkOrderSyncActionDTO[]
}

/** One work order's action: what the sync did or would do, to which order, and each field it changes. */
function SyncActionItem({ action }: { action: WorkOrderSyncActionDTO }) {
	return (
		<li className="border-ds-border border-b py-3 last:border-b-0">
			<div className="flex flex-wrap items-center gap-2 text-ds-sm">
				<Badge size="sm" variant={ACTION_VARIANT[action.action] ?? 'muted'}>
					{humanize(action.action)}
				</Badge>
				{action.shadow ? (
					<Badge size="sm" variant="warning">
						shadow
					</Badge>
				) : null}
				{action.backfill ? (
					<Badge size="sm" variant="muted">
						backfill
					</Badge>
				) : null}
				<span>{humanize(action.role)}</span>
				<span className="font-mono text-ds-xs text-ds-text-muted">{action.externalRef}</span>
				{action.workOrderId ? (
					<Link to={`/work-orders/${action.workOrderId}`} className="text-ds-primary hover:underline">
						Open work order
					</Link>
				) : null}
				<span className="ml-auto text-ds-text-muted">
					v{action.bundleVersion} · {formatUtc(action.computedAt)}
				</span>
			</div>
			{action.fieldChanges.length ? (
				<dl className="mt-2 space-y-0.5 pl-3 text-ds-sm">
					{action.fieldChanges.map((change) => (
						<div key={change.field} className="flex flex-wrap gap-1.5">
							<dt className="font-medium">{fieldLabel(change.field)}:</dt>
							<dd className="text-ds-text-muted">
								{formatFieldValue(change.before)} → {formatFieldValue(change.after)}
							</dd>
						</div>
					))}
				</dl>
			) : null}
			{action.warnings.length ? (
				<ul aria-label="Warnings" className="mt-2 space-y-0.5 pl-3 text-ds-sm">
					{action.warnings.map((warning) => (
						<li key={`${warning.code}:${warning.message}`}>
							<span className="font-mono text-ds-xs text-ds-text-muted">{warning.code}</span> {warning.message}
						</li>
					))}
				</ul>
			) : null}
		</li>
	)
}

/**
 * The actions the work-order sync recorded for a bundle version, one per work order: in shadow
 * mode what it would do, in live mode what it did. The review an admin does before going live.
 */
export function SyncActionsList({ actions }: SyncActionsListProps) {
	if (actions.length === 0) {
		return (
			<EmptyState
				title="No sync actions yet"
				description="The work-order sync has not recorded an action for this bundle's version."
			/>
		)
	}
	return (
		<ul aria-label="Sync actions">
			{actions.map((action) => (
				<SyncActionItem key={action.id} action={action} />
			))}
		</ul>
	)
}
